/**
 * `UcpShippingTaxEditor` — the "Shipping & tax" part of the UCP checkout
 * row (ADR-0215 §8). Flat, merchant-entered rates: shipping options
 * (name, price, optional countries, optional free-over) and tax rules
 * (country, optional region, rate, whether shipping is taxed). Saved as
 * one PUT; the server validates strictly and the profile advertises the
 * fulfillment extension once at least one shipping option exists.
 *
 * Amounts are edited in major units and stored in minor units.
 */

import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { UcpFulfillment, UcpShippingOption, UcpTaxRule } from '../../api/site-ucp.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

interface OptionDraft { id: string; title: string; description: string; amount: string; countries: string; free_over: string }
interface RuleDraft { country: string; region: string; rate: string; tax_shipping: boolean }

const toMinor = (major: string): number | null => {
  const n = Number(major.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
const toMajor = (minor: number): string => (minor / 100).toFixed(2);
const slug = (title: string): string => title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'option';

export function draftsFromConfig(f: UcpFulfillment): { options: OptionDraft[]; rules: RuleDraft[] } {
  return {
    options: f.shipping_options.map((o) => ({ id: o.id, title: o.title, description: o.description ?? '', amount: toMajor(o.amount_minor), countries: (o.countries ?? []).join(', '), free_over: o.free_over_minor !== undefined ? toMajor(o.free_over_minor) : '' })),
    rules: f.tax_rules.map((r) => ({ country: r.country, region: r.region ?? '', rate: (r.rate_bp / 100).toString(), tax_shipping: r.tax_shipping === true })),
  };
}

/** Drafts → wire shape; `null` with a message when something is not a number. */
export function configFromDrafts(options: OptionDraft[], rules: RuleDraft[]): { config: UcpFulfillment } | { error: string } {
  const shipping_options: UcpShippingOption[] = [];
  const seen = new Set<string>();
  for (const o of options) {
    const title = o.title.trim();
    if (title.length === 0) return { error: 'Every shipping option needs a name.' };
    const amount_minor = toMinor(o.amount);
    if (amount_minor === null) return { error: `"${title}": the price is not a number.` };
    let id = o.id.trim().length > 0 ? o.id.trim() : slug(title);
    while (seen.has(id)) id = `${id}_2`;
    seen.add(id);
    const countries = o.countries.split(/[,\s]+/).map((c) => c.trim().toUpperCase()).filter((c) => c.length > 0);
    if (countries.some((c) => !/^[A-Z]{2}$/.test(c))) return { error: `"${title}": countries must be two-letter codes like US, CA, DE.` };
    const free_over_minor = o.free_over.trim().length === 0 ? null : toMinor(o.free_over);
    if (o.free_over.trim().length > 0 && free_over_minor === null) return { error: `"${title}": the free-over amount is not a number.` };
    shipping_options.push({
      id, title, amount_minor,
      ...(o.description.trim().length > 0 ? { description: o.description.trim() } : {}),
      ...(countries.length > 0 ? { countries } : {}),
      ...(free_over_minor !== null ? { free_over_minor } : {}),
    });
  }
  const tax_rules: UcpTaxRule[] = [];
  for (const r of rules) {
    const country = r.country.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) return { error: 'Tax rules need a two-letter country code.' };
    const pct = Number(r.rate.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0 || pct > 50) return { error: `Tax rate for ${country} must be between 0 and 50 %.` };
    tax_rules.push({ country, rate_bp: Math.round(pct * 100), ...(r.region.trim().length > 0 ? { region: r.region.trim().toUpperCase() } : {}), ...(r.tax_shipping ? { tax_shipping: true } : {}) });
  }
  return { config: { shipping_options, tax_rules } };
}

export function UcpShippingTaxEditor({ initial, saving, onSave }: { initial: UcpFulfillment; saving: boolean; onSave: (config: UcpFulfillment) => void }): JSX.Element {
  const C = useC();
  const start = draftsFromConfig(initial);
  const [options, setOptions] = useState<OptionDraft[]>(start.options);
  const [rules, setRules] = useState<RuleDraft[]>(start.rules);
  const [error, setError] = useState<string | null>(null);

  const input = { padding: '7px 9px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, color: C.text, fontFamily: sansFont, fontSize: 12.5, width: '100%' } as const;
  const kicker = { fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5 } as const;
  const ghost = { background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, color: C.text, fontFamily: sansFont, fontSize: 12, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 } as const;

  const setOpt = (i: number, patch: Partial<OptionDraft>): void => setOptions((prev) => prev.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const setRule = (i: number, patch: Partial<RuleDraft>): void => setRules((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-4 pt-2" data-testid="ucp-shipping-tax">
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        Flat rates a platform will quote for this store. Add at least one shipping option to accept physical items; leave it empty to sell digital items only. Tax is applied to the order total by the buyer&apos;s country (and region, when you add one).
      </div>

      <div>
        <div style={{ ...kicker, marginBottom: 6 }}>SHIPPING OPTIONS</div>
        <div className="space-y-2">
          {options.map((o, i) => (
            <div key={i} className="p-3 space-y-2" style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }} data-testid="ucp-shipping-option">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input aria-label="Option name" placeholder="Standard" value={o.title} onChange={(e) => setOpt(i, { title: e.target.value })} style={input} />
                <input aria-label="Price" placeholder="5.99" inputMode="decimal" value={o.amount} onChange={(e) => setOpt(i, { amount: e.target.value })} style={input} />
                <input aria-label="Free over" placeholder="Free over (optional)" inputMode="decimal" value={o.free_over} onChange={(e) => setOpt(i, { free_over: e.target.value })} style={input} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-2 items-center">
                <input aria-label="Description" placeholder="Arrives in 5–8 business days (optional)" value={o.description} onChange={(e) => setOpt(i, { description: e.target.value })} style={input} />
                <input aria-label="Countries" placeholder="Countries: US, CA (blank = all)" value={o.countries} onChange={(e) => setOpt(i, { countries: e.target.value })} style={input} />
                <button type="button" aria-label="Remove option" onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))} style={{ ...ghost, padding: 7 }}><Trash2 size={13} color={C.muted} /></button>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setOptions((prev) => [...prev, { id: '', title: '', description: '', amount: '', countries: '', free_over: '' }])} style={ghost}>
            <Plus size={13} /> Add shipping option
          </button>
        </div>
      </div>

      <div>
        <div style={{ ...kicker, marginBottom: 6 }}>TAX</div>
        <div className="space-y-2">
          {rules.map((r, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto_auto] gap-2 items-center" data-testid="ucp-tax-rule">
              <input aria-label="Country" placeholder="US" value={r.country} onChange={(e) => setRule(i, { country: e.target.value })} style={input} maxLength={2} />
              <input aria-label="Region" placeholder="Region (optional): CA" value={r.region} onChange={(e) => setRule(i, { region: e.target.value })} style={input} />
              <input aria-label="Rate percent" placeholder="8.25" inputMode="decimal" value={r.rate} onChange={(e) => setRule(i, { rate: e.target.value })} style={input} />
              <label className="flex items-center gap-1.5" style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={r.tax_shipping} onChange={(e) => setRule(i, { tax_shipping: e.target.checked })} style={{ accentColor: C.lime }} /> on shipping
              </label>
              <button type="button" aria-label="Remove tax rule" onClick={() => setRules((prev) => prev.filter((_, j) => j !== i))} style={{ ...ghost, padding: 7 }}><Trash2 size={13} color={C.muted} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setRules((prev) => [...prev, { country: '', region: '', rate: '', tax_shipping: false }])} style={ghost}>
            <Plus size={13} /> Add tax rule
          </button>
        </div>
      </div>

      {error !== null ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>{error}</div> : null}
      <button
        type="button"
        disabled={saving}
        onClick={() => {
          const r = configFromDrafts(options, rules);
          if ('error' in r) { setError(r.error); return; }
          setError(null);
          onSave(r.config);
        }}
        className="px-3 py-2"
        style={{ background: C.lime, color: '#0B0B0B', border: 'none', borderRadius: 2, fontFamily: sansFont, fontSize: 12, fontWeight: 500, opacity: saving ? 0.6 : 1 }}
      >
        {saving ? 'Saving…' : 'Save shipping & tax'}
      </button>
    </div>
  );
}
